import { useQuery } from "@tanstack/react-query";
import { agentPresenceApi, type AgentPresence } from "@/api/agent-presence";

export type AgentGroup = "needs-you" | "running" | "idle";

export type NeedsYouReason = "Approval" | "Message" | "Error";

/**
 * Per-agent signals for the grouped sidebar list. Polled on the same cadence
 * as the notifications bell so groups re-sort live; a failed poll keeps the
 * last good data rather than collapsing every agent back to idle.
 */
export function useAgentPresence(companyId: string) {
  return useQuery({
    queryKey: ["agent-presence", companyId],
    queryFn: () => agentPresenceApi.byCompany(companyId),
    enabled: !!companyId,
    refetchInterval: 15_000,
    refetchOnWindowFocus: true,
    staleTime: 10_000,
    placeholderData: (prev) => prev,
  });
}

const EMPTY: AgentPresence = {
  pendingApprovals: 0,
  unreadDirect: 0,
  activeRuns: 0,
  activeTasks: 0,
  lastFailureAt: null,
  lastFailureKind: null,
  lastActivityAt: null,
};

export function presenceFor(
  map: Record<string, AgentPresence> | undefined,
  agentId: string,
): AgentPresence {
  return map?.[agentId] ?? EMPTY;
}

/** A failure only counts as "needs you" while it is recent (last 24h). */
function recentFailure(p: AgentPresence): boolean {
  if (!p.lastFailureAt) return false;
  return Date.now() - new Date(p.lastFailureAt).getTime() < 24 * 3_600_000;
}

export function needsYouReason(p: AgentPresence): NeedsYouReason | null {
  if (p.pendingApprovals > 0) return "Approval";
  if (p.unreadDirect > 0) return "Message";
  if (recentFailure(p)) return "Error";
  return null;
}

export function activeTaskCount(p: AgentPresence): number {
  return p.activeRuns + p.activeTasks;
}

export function groupFor(p: AgentPresence): AgentGroup {
  if (needsYouReason(p)) return "needs-you";
  if (activeTaskCount(p) > 0) return "running";
  return "idle";
}
